import { useState, useRef, useEffect } from "react";
import { mediaSrc } from "../lib/api";
import { ChevronLeft, ChevronRight, Layers, Star } from "lucide-react";

// Swipeable multi-asset viewer: slides stay mounted in one track, so swiping never reloads or reflows.
export default function MediaCarousel({
  assets,
  compact = false,
  heightClass = "media-fit",
  onImageClick,
  coverIndex = 0,
  onSetCover = null,
  testid = "carousel",
}) {
  const [i, setI] = useState(0);
  const [drag, setDrag] = useState(0);
  const [ratio, setRatio] = useState(null);
  const touch = useRef(null);
  const moved = useRef(false);
  const boxRef = useRef(null);
  const n = assets?.length || 0;

  const firstKey = assets?.[0]?.storage_path || assets?.[0]?.url;
  useEffect(() => { setI(0); setRatio(null); }, [n, firstKey]);
  if (!n) return null;

  const go = (d, e) => { if (e) { e.preventDefault(); e.stopPropagation(); } setI((p) => (p + d + n) % n); };
  const onStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, horiz: null, t: Date.now() };
    moved.current = false;
  };
  const onMove = (e) => {
    const s = touch.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (s.horiz === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) s.horiz = Math.abs(dx) > Math.abs(dy);
    if (!s.horiz) return;
    moved.current = true;
    const atEdge = (i === 0 && dx > 0) || (i === n - 1 && dx < 0);
    setDrag(atEdge ? dx / 3 : dx);
  };
  const onEnd = () => {
    const s = touch.current;
    touch.current = null;
    if (!s || !s.horiz) return setDrag(0);
    const w = boxRef.current?.offsetWidth || 1;
    const fast = Math.abs(drag) > 30 && Date.now() - s.t < 250;
    if ((Math.abs(drag) > w * 0.2 || fast) && !((i === 0 && drag > 0) || (i === n - 1 && drag < 0))) setI(i + (drag < 0 ? 1 : -1));
    setDrag(0);
  };
  const onFirstLoad = (w, h) => { if (!ratio && w && h) setRatio(Math.min(Math.max(w / h, 0.5), 2)); };

  return (
    <div data-testid={testid} className="relative w-full select-none">
      <div
        ref={boxRef}
        className={`relative bg-black overflow-hidden w-full ${heightClass}`}
        style={{ aspectRatio: ratio || (compact ? "4 / 5" : "1 / 1"), touchAction: "pan-y" }}
        onTouchStart={onStart}
        onTouchMove={onMove}
        onTouchEnd={onEnd}
        onTouchCancel={onEnd}
        onClickCapture={(e) => { if (moved.current) { e.preventDefault(); e.stopPropagation(); moved.current = false; } }}
      >
        <div
          className="flex h-full"
          style={{
            transform: `translate3d(calc(${-i * 100}% + ${drag}px), 0, 0)`,
            transition: drag ? "none" : "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {assets.map((a, idx) => {
            const near = Math.abs(idx - i) <= 1;
            return (
              <div key={a.storage_path || a.url || idx} className={`w-full h-full shrink-0 flex items-center justify-center ${onImageClick ? "cursor-pointer" : ""}`} onClick={onImageClick} data-testid={`${testid}-slide-${idx}`}>
                {a.media_type === "video" ? (
                  near ? (
                    <video
                      src={mediaSrc(a.url)}
                      poster={a.thumb_url ? mediaSrc(a.thumb_url) : undefined}
                      controls={idx === i}
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={idx === 0 ? (e) => onFirstLoad(e.target.videoWidth, e.target.videoHeight) : undefined}
                      className="w-full h-full object-contain bg-black"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : a.thumb_url ? <img src={mediaSrc(a.thumb_url)} alt="" className="w-full h-full object-contain" draggable={false} /> : null
                ) : (
                  <img
                    src={mediaSrc(compact ? (a.thumb_url || a.url) : a.url)}
                    alt={`${idx + 1} of ${n}`}
                    loading={near ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                    onLoad={idx === 0 ? (e) => onFirstLoad(e.target.naturalWidth, e.target.naturalHeight) : undefined}
                    className="w-full h-full object-contain bg-black"
                  />
                )}
              </div>
            );
          })}
        </div>

        <span className="absolute top-3 right-3 z-10 bg-black/70 text-white px-2.5 py-1 rounded-full font-mono text-[11px] flex items-center gap-1 pointer-events-none">
          <Layers size={11} /> {i + 1}/{n}
        </span>

        {onSetCover && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (i !== coverIndex) onSetCover(i); }}
            disabled={i === coverIndex}
            data-testid={`${testid}-set-cover`}
            className={`absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full font-mono text-[11px] flex items-center gap-1.5 transition-colors ${
              i === coverIndex ? "bg-[#00E5FF] text-black cursor-default" : "bg-black/70 text-white hover:bg-black/90 cursor-pointer"
            }`}
          >
            <Star size={11} fill={i === coverIndex ? "black" : "none"} />
            {i === coverIndex ? "Cover" : "Set as cover"}
          </button>
        )}

        {n > 1 && (
          <>
            <button type="button" onClick={(e) => go(-1, e)} data-testid={`${testid}-prev`} aria-label="Previous" className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 items-center justify-center text-white transition-colors">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={(e) => go(1, e)} data-testid={`${testid}-next`} aria-label="Next" className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 items-center justify-center text-white transition-colors">
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {compact && n > 1 && (
          <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5 pointer-events-none">
            {assets.map((_, idx) => (
              <span key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-5 bg-[#00E5FF]" : "w-1.5 bg-white/50"}`} />
            ))}
          </div>
        )}
      </div>

      {!compact && n > 1 && (
        <div className="flex gap-2 mt-2 overflow-x-auto py-1" data-testid={`${testid}-strip`}>
          {assets.map((a, idx) => (
            <button key={idx} type="button" onClick={() => setI(idx)} data-testid={`${testid}-thumb-${idx}`} className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === i ? "border-[#00E5FF]" : "border-transparent opacity-55 hover:opacity-100"}`}>
              <img src={mediaSrc(a.thumb_url || a.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
              {idx === coverIndex && (
                <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#00E5FF] flex items-center justify-center" title="Album cover">
                  <Star size={9} className="text-black" fill="black" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
